import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Alert, Card, CardBody } from '../components/ui';
import { ArbieMascot } from '../components/ArbieMascot';

const BASE_URL = import.meta.env.VITE_API_URL || '';

const CONFIRMATION =
  'If an account exists with this email, a password reset link has been sent. The link expires in 1 hour.';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    setIsLoading(true);
    try {
      await fetch(`${BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
    } catch {
      // Deliberately swallowed. Showing a network error here would let a caller
      // distinguish outcomes; the confirmation must be unconditional.
    } finally {
      setIsLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-12">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <ArbieMascot className="mx-auto h-16 w-16" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-gray-100">
            Reset your password
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            We'll email you a link to choose a new one.
          </p>
        </div>

        <Card>
          <CardBody>
            {submitted ? (
              <Alert variant="success">{CONFIRMATION}</Alert>
            ) : (
              <>
                {error && (
                  <Alert variant="error" className="mb-4">
                    {error}
                  </Alert>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Email
                    </label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      disabled={isLoading}
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={isLoading} loading={isLoading}>
                    Send reset link
                  </Button>
                </form>
              </>
            )}

            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-sm text-amber-600 hover:text-amber-700 dark:text-amber-500"
              >
                Back to sign in
              </Link>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
